// client/src/components/forms/PublicFormRenderer.jsx
//
// Renders one interest / lead form from its public schema (GET
// /api/forms/public/:tenantSlug/:formId) and handles the submit, field
// errors, success message / redirect, and the optional push opt-in after
// submit. Used by the hosted page (/f/...), the iframe embed (/embed/f/...),
// the event page section, and the builder's live preview (preview = true:
// nothing is sent). Growth Phase 4, feature_key "forms".

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  browserSupportsPush, enableFormPush, readVisitorId, submitPublicForm,
} from '../../services/formsApi';
import './Forms.css';

const INPUT_TYPE = { text: 'text', email: 'email', phone: 'tel', date: 'date' };

function initialValues(fields) {
  const v = {};
  for (const f of fields || []) v[f.field_key] = f.field_type === 'checkbox' ? false : '';
  return v;
}

export default function PublicFormRenderer({ form, tenantSlug, preview = false, embedded = false }) {
  const { t } = useTranslation();
  const fields = useMemo(() => form?.fields || [], [form]);
  const [values, setValues] = useState(() => initialValues(fields));
  const [emailOptIn, setEmailOptIn] = useState(!!form?.emailOptInDefault);
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [formError, setFormError] = useState('');
  const [result, setResult] = useState(null);
  const [push, setPush] = useState('idle');
  // Volunteer forms list the event's open shifts (Roadmap Phase 5.2).
  const [shiftIds, setShiftIds] = useState([]);

  if (!form) return null;
  const idBase = `fm-${form.id || 'preview'}`;

  const set = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (preview) return;
    setStatus('submitting');
    setFormError('');
    setErrors({});
    try {
      const res = await submitPublicForm(tenantSlug, form.id, {
        data: values,
        emailOptIn: form.consentText ? emailOptIn : false,
        website: honeypot,
        os_vid: readVisitorId(),
        ...(form.shifts && form.shifts.length ? { shiftIds } : {}),
      });
      setResult(res);
      setStatus('done');
      if (res.redirectUrl && !embedded) window.location.assign(res.redirectUrl);
    } catch (err) {
      setStatus('idle');
      if (err.status === 400 && err.data?.fields?.shifts) {
        setErrors(err.data.fields);
        setFormError(err.data.fields.shifts);
      } else if (err.status === 400 && err.data?.fields) {
        setErrors(err.data.fields);
        setFormError(t('forms.public.fixFields', 'Check the highlighted fields.'));
      } else if (err.status === 429) {
        setFormError(t('forms.public.tooMany', 'Too many submissions. Please wait a minute and try again.'));
      } else {
        setFormError(t('forms.public.submitError', 'Something went wrong. Please try again.'));
      }
    }
  };

  const turnOnPush = async () => {
    setPush('working');
    try {
      await enableFormPush(tenantSlug, form.id, result.pushToken);
      setPush('on');
    } catch (err) {
      const code = err?.data?.code;
      if (err.message === 'denied') setPush('denied');
      else if (code === 'SIGN_IN_REQUIRED' || code === 'EMAIL_IN_USE') setPush('account');
      else setPush('error');
    }
  };

  if (status === 'done' && result) {
    return (
      <div className="fm-card" role="status">
        <p className="fm-success">{result.message}</p>
        {result.volunteer && result.volunteer.full && result.volunteer.full.length > 0 && (
          <p className="fm-error">{t('forms.public.shiftsFilled', 'One of your shifts filled up just before you signed up; the others are confirmed.')}</p>
        )}
        {result.redirectUrl && embedded && (
          <p><a className="fm-link" href={result.redirectUrl} target="_top" rel="noopener">{t('forms.public.continue', 'Continue')}</a></p>
        )}
        {form.pushPrompt && result.pushToken && browserSupportsPush() && !embedded && (
          <div className="fm-push">
            {push === 'on' ? (
              <p>{t('forms.public.pushOn', 'Notifications are on for this browser.')}</p>
            ) : (
              <>
                <p>{t('forms.public.pushAsk', 'Want a browser notification when there is news?')}</p>
                <button type="button" className="fm-btn secondary" onClick={turnOnPush} disabled={push === 'working'}>
                  {push === 'working' ? t('forms.public.pushWorking', 'Turning on…') : t('forms.public.pushButton', 'Turn on notifications')}
                </button>
                {push === 'denied' && <p className="fm-error">{t('forms.public.pushDenied', 'Notifications are blocked in this browser.')}</p>}
                {push === 'error' && <p className="fm-error">{t('forms.public.pushError', 'Could not turn on notifications.')}</p>}
                {push === 'account' && (
                  <p className="fm-error">{t('forms.public.pushAccount', 'This email has an account. Sign in to your member page to turn on notifications.')}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <form className="fm-card" onSubmit={onSubmit} noValidate aria-label={form.title || t('forms.public.formLabel', 'Form')}>
      {form.title && <h2 className="fm-title">{form.title}</h2>}
      {form.intro && <p className="fm-intro">{form.intro}</p>}

      {fields.map((f) => {
        const id = `${idBase}-${f.field_key}`;
        const err = errors[f.field_key];
        const common = {
          id,
          name: f.field_key,
          'aria-invalid': err ? 'true' : undefined,
          'aria-describedby': err ? `${id}-err` : undefined,
          required: f.required || undefined,
        };
        if (f.field_type === 'rating' || f.field_type === 'nps') {
          // Survey scales (Growth Phase 9): rating 1-5, NPS 0-10, as a radio group.
          const scale = f.field_type === 'nps' ? Array.from({ length: 11 }, (_, i) => i) : [1, 2, 3, 4, 5];
          return (
            <fieldset key={f.field_key} className="fm-field fm-scale" aria-invalid={err ? 'true' : undefined}
              aria-describedby={err ? `${id}-err` : undefined}>
              <legend>{f.label}{f.required && <span className="fm-req" aria-hidden="true"> *</span>}</legend>
              <div className="fm-scale-options">
                {scale.map((n) => (
                  <label key={n} className={`fm-scale-option${String(values[f.field_key]) === String(n) ? ' on' : ''}`}>
                    <input type="radio" name={f.field_key} value={n} checked={String(values[f.field_key]) === String(n)}
                      onChange={() => set(f.field_key, String(n))} />
                    <span>{n}</span>
                  </label>
                ))}
              </div>
              {f.field_type === 'nps' && (
                <div className="fm-scale-ends" aria-hidden="true">
                  <span>{t('forms.public.npsLow', 'Not likely')}</span>
                  <span>{t('forms.public.npsHigh', 'Very likely')}</span>
                </div>
              )}
              {err && <span id={`${id}-err`} className="fm-error">{err}</span>}
            </fieldset>
          );
        }
        if (f.field_type === 'checkbox') {
          return (
            <div key={f.field_key} className="fm-field fm-check">
              <label htmlFor={id}>
                <input type="checkbox" {...common} checked={!!values[f.field_key]} onChange={(e) => set(f.field_key, e.target.checked)} />
                <span>{f.label}{f.required && <span className="fm-req" aria-hidden="true"> *</span>}</span>
              </label>
              {err && <span id={`${id}-err`} className="fm-error">{err}</span>}
            </div>
          );
        }
        return (
          <div key={f.field_key} className="fm-field">
            <label htmlFor={id}>{f.label}{f.required && <span className="fm-req" aria-hidden="true"> *</span>}</label>
            {f.field_type === 'textarea' && (
              <textarea {...common} rows={4} value={values[f.field_key] || ''} onChange={(e) => set(f.field_key, e.target.value)} />
            )}
            {f.field_type === 'select' && (
              <select {...common} value={values[f.field_key] || ''} onChange={(e) => set(f.field_key, e.target.value)}>
                <option value="">{t('forms.public.choose', 'Choose…')}</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {INPUT_TYPE[f.field_type] && (
              <input
                type={INPUT_TYPE[f.field_type]}
                {...common}
                autoComplete={f.field_type === 'email' ? 'email' : f.field_type === 'phone' ? 'tel' : undefined}
                value={values[f.field_key] || ''}
                onChange={(e) => set(f.field_key, e.target.value)}
              />
            )}
            {err && <span id={`${id}-err`} className="fm-error">{err}</span>}
          </div>
        );
      })}

      {form.shifts && form.shifts.length > 0 && (
        <fieldset className="fm-field fm-shifts" aria-describedby={errors.shifts ? `${idBase}-shifts-err` : undefined}>
          <legend className="fm-label">{t('forms.public.shifts', 'Pick your shifts')}</legend>
          {form.shifts.map((sh) => (
            <label key={sh.id} className="fm-check fm-shift" htmlFor={`${idBase}-shift-${sh.id}`}>
              <input
                id={`${idBase}-shift-${sh.id}`}
                type="checkbox"
                disabled={sh.full}
                checked={shiftIds.includes(sh.id)}
                onChange={(e) => {
                  setShiftIds((ids) => (e.target.checked ? [...ids, sh.id] : ids.filter((x) => x !== sh.id)));
                  if (errors.shifts) setErrors((prev) => { const n = { ...prev }; delete n.shifts; return n; });
                }}
              />
              <span>
                <strong>{sh.role}</strong> · {sh.when}{sh.location ? ` · ${sh.location}` : ''}
                {sh.full
                  ? ` (${t('forms.public.shiftFull', 'full')})`
                  : sh.left !== null && sh.left !== undefined ? ` (${t('forms.public.shiftLeft', '{{count}} places left', { count: sh.left })})` : ''}
                {sh.description && <span className="fm-muted" style={{ display: 'block' }}>{sh.description}</span>}
              </span>
            </label>
          ))}
          {errors.shifts && <span id={`${idBase}-shifts-err`} className="fm-error">{errors.shifts}</span>}
        </fieldset>
      )}

      {form.consentText && (
        <div className="fm-field fm-check">
          <label htmlFor={`${idBase}-optin`}>
            <input id={`${idBase}-optin`} type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} />
            <span>{form.consentText}</span>
          </label>
        </div>
      )}

      {/* Honeypot: hidden from people and screen readers; bots fill it. */}
      <div className="fm-hp" aria-hidden="true">
        <label htmlFor={`${idBase}-website`}>Website</label>
        <input id={`${idBase}-website`} type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      {formError && <p className="fm-error" role="alert">{formError}</p>}
      <button type="submit" className="fm-btn" disabled={preview || status === 'submitting'}>
        {status === 'submitting' ? t('forms.public.sending', 'Sending…') : (form.submitLabel || t('forms.public.submit', 'Submit'))}
      </button>
      {preview && <p className="fm-muted">{t('forms.preview.note', 'Preview only: submissions are off here.')}</p>}
    </form>
  );
}
